// ════════════════════════════════════════════════════════════════════
// TERROR — H5's FACE · THE FACTION WAR BANNER
// ────────────────────────────────────────────────────────────────────
//   <FactionWarBanner uid={uid} faction={profile?.faction} />
//
// A strip of the Plaza that says who is winning the week, how long is
// left, and — if you are on the winning side and you actually fought —
// dresses you in it.
//
// ── THIS IS ALSO THE APP LAYER THAT PAYS ─────────────────────────────
// factionWars.js is a leaf: it decides who won and records that the
// week is settled, but it grants nothing, because granting means
// importing progression and progression calls factionWars from inside
// the win path. So the SPOILS are paid here, where importing both is
// free. The banner is the only mounted thing that reliably sees the
// Plaza, which makes it the natural settler.
//
// ── THE SETTLEMENT IS OPPORTUNISTIC AND SAYS SO ──────────────────────
// There is no server. The first player to open the Plaza after a week
// ends settles it, once, transaction-guarded. If nobody plays for three
// weeks, three weeks settle in a row the next time somebody does —
// walkPastWeeks() below handles exactly that, bounded, oldest first, so
// titles arrive in the order they were won.
//
// ── THE TINT EXPIRES BY ITSELF ───────────────────────────────────────
// Nothing stores "you are tinted until Monday." The tint is derived from
// the most recent SETTLED week every time it is read, so it stops being
// true the moment the next week settles. No expiry job, no stale flag,
// nothing to clean up.
// ════════════════════════════════════════════════════════════════════

import React, { useEffect, useState, useRef } from "react";
import { GM } from "./theme";
import { useLang } from "./lang"; // M9 — the worked conversion; see lang.js
import { FACTIONS } from "./FactionHall";
import { grantCosmetic } from "./progression";
import {
  WAR_FACTIONS, currentWeekKey, previousWeekKey, msLeftIn,
  listenToWeek, readWeek, standingsOf, settleWeek, warTitleIdFor,
  warTintClaim, stampSpoils, contributedTo,
} from "./factionWars";

// How many un-settled past weeks one mount is willing to walk. A cap,
// not a rule — it exists so a profile that has been away for a year
// cannot turn one Plaza open into fifty-two round trips. Anything older
// than this settles on some later visit, and the tally it settles from
// is permanent, so nothing is lost by being late.
const MAX_BACKFILL_WEEKS = 6;

// The roster in factionWars is three ids; the names and colours live in
// FactionHall. Two tables that must agree get a function, not a hope.
export const auditFactions = () => {
  const known = Object.keys(FACTIONS);
  return {
    missingMeta: WAR_FACTIONS.filter((id) => !known.includes(id)),
    notAtWar: known.filter((id) => !WAR_FACTIONS.includes(id)),
    ok: WAR_FACTIONS.every((id) => known.includes(id)),
  };
};

const metaOf = (id) => FACTIONS[id] || { short: id, name: id, color: GM.vellumDim };

// M9 — note what the conversion did to this function: the three
// branches became three KEYS, because "2d 4h left" and "40m left" are
// not one sentence with a variable in it, they are three sentences with
// three different grammars. A language that counts differently gets
// three rows to work with instead of one string it cannot fix.
const countdown = (t, ms) => {
  if (ms <= 0) return t("wars.settling", "settling");
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return t("wars.left.days", "{d}d {h}h left", { d, h });
  if (h > 0) return t("wars.left.hours", "{h}h {m}m left", { h, m });
  return t("wars.left.minutes", "{m}m left", { m });
};

export default function FactionWarBanner({ uid, faction, compact = false }) {
  const { t } = useLang();
  const [week, setWeek] = useState({});
  const [key] = useState(currentWeekKey);
  const [last, setLast] = useState(null);   // { key, settled, week }
  const [left, setLeft] = useState(() => msLeftIn(key));
  const settling = useRef(false);

  // Live current week.
  useEffect(() => listenToWeek(key, setWeek), [key]);

  // A one-minute tick, not one per second: this is a countdown measured
  // in days, and a per-second interval on the Plaza is a per-second
  // re-render for a number that changes 1440 times a week.
  useEffect(() => {
    const t = setInterval(() => setLeft(msLeftIn(key)), 60000);
    return () => clearInterval(t);
  }, [key]);

  // Settle whatever is owed, oldest first, then read the newest settled
  // week for the tint. Runs once per mount and guards itself — a second
  // Plaza tab does not double-pay, because settleWeek's transaction
  // decides who settled and everyone else is told `already`.
  useEffect(() => {
    if (!uid || settling.current) return;
    settling.current = true;
    let dead = false;

    (async () => {
      // Walk back to the oldest unsettled week inside the cap, then
      // settle forward, so a returning player collects in the order
      // they earned.
      const keys = [];
      let k = previousWeekKey(key);
      for (let i = 0; i < MAX_BACKFILL_WEEKS && k; i++) {
        keys.unshift(k);
        k = previousWeekKey(k);
      }

      let newestSettled = null;
      for (const wk of keys) {
        if (dead) return;
        const data = await readWeek(wk);
        if (!data || !Object.keys(data).length) continue;

        let settled = data.settled;
        if (!settled) {
          const res = await settleWeek(wk);
          if (res.ok && !res.already) {
            settled = { at: Date.now(), winner: res.winner, margin: res.margin };
          } else if (res.ok && res.already) {
            settled = res.settled;
          }
        }
        if (!settled?.winner) continue;
        newestSettled = { key: wk, settled, week: data };

        // ── PAY, if this player is owed. Append-only and idempotent:
        // grantCosmetic returns alreadyUnlocked for a title they hold,
        // so re-walking a week costs one read and grants nothing.
        if (faction === settled.winner && contributedTo(data, uid)) {
          const m = metaOf(settled.winner);
          await grantCosmetic(uid, warTitleIdFor(settled.winner), {
            slot: "title",
            // A week's spoil sits between a family's Legendary and its
            // Fable on the cosmetic ladder, deliberately: harder than
            // finishing a starter arc, easier than closing a lifetime
            // one, and gone from the shelf the moment somebody else
            // takes a week. It is NOT a titles.js tier — war titles are
            // not families, which is why auditTitleStrings skips them.
            rarity: "epic",
            source: `factionWars:${wk}`,
          });
          await stampSpoils(uid, wk, settled.winner);
          if (!dead) console.log(`[factionWars] ${wk}: ${m.short} took the week.`);
        }
      }
      if (!dead) setLast(newestSettled);
    })().catch((e) => console.warn("[factionWars] settle walk failed:", e?.message));

    // The guard MUST reset with the effect. Without this, a player who
    // joins a faction mid-session re-runs the effect (faction is a dep)
    // and finds settling.current still true from the unfactioned pass —
    // so they would never collect a spoil for as long as the tab is
    // open. Found in the critique pass.
    return () => { dead = true; settling.current = false; };
  }, [uid, faction, key]);

  const st = standingsOf(week);
  const claim = last ? warTintClaim({ settled: last.settled, week: last.week, uid, faction }) : { active: false };
  const tint = claim.active ? metaOf(claim.faction).color : null;

  const leadMeta = st.winner ? metaOf(st.winner) : null;

  return (
    <div style={{
      border: `1px solid ${tint || GM.pageBorder}`,
      borderRadius: 8,
      background: tint
        ? `linear-gradient(180deg, ${tint}14, ${GM.pageMid})`
        : GM.pageMid,
      boxShadow: tint ? `0 0 18px ${tint}22` : "none",
      padding: compact ? "8px 10px" : "10px 14px",
      fontFamily: GM.font,
      transition: "border-color 400ms ease, box-shadow 400ms ease",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 9, letterSpacing: 3, color: GM.inkGoldBright, fontWeight: 900 }}>
          ⚔ {t("wars.header", "FACTION WARS")}
        </span>
        <span style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 1 }}>{key}</span>
        <span style={{ marginLeft: "auto", fontSize: 9, color: GM.vellumFaint }}>
          {countdown(t, left)}
        </span>
      </div>

      {/* THE LEAD LINE — never invents a leader out of a tie. */}
      <div style={{ fontSize: 11.5, fontStyle: "italic", color: GM.vellumDim, marginBottom: 8, lineHeight: 1.5 }}>
        {st.empty
          ? t("wars.empty", "Nothing won yet this week. The board opens with the first Solrend.")
          : st.tied.length
            ? t("wars.deadHeat", "Dead heat — {who} are level at {n}.",
                { who: st.tied.map((f) => metaOf(f).short).join(" and "), n: st.rows[0].wins })
            : t("wars.lead", "{faction} leads by {margin}.",
                { faction: leadMeta.short, margin: st.margin })}
      </div>

      {/* THE BARS — one row per faction, share of the week's wins. */}
      <div style={{ display: "grid", gap: 5 }}>
        {st.rows.map((r) => {
          const m = metaOf(r.id);
          const mine = faction === r.id;
          return (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{
                fontSize: 10, width: 62, flex: "0 0 auto",
                color: mine ? m.color : GM.vellumDim,
                fontWeight: mine ? 900 : 400,
              }}>
                {m.short}
              </span>
              <div style={{
                flex: 1, height: 6, borderRadius: 3, overflow: "hidden",
                background: GM.page, border: `1px solid ${GM.pageBorder}`,
              }}>
                <div style={{
                  width: `${Math.round(r.share * 100)}%`, height: "100%",
                  background: `linear-gradient(90deg, ${m.color}88, ${m.color})`,
                  transition: "width 600ms ease",
                }} />
              </div>
              <span style={{ fontSize: 10, width: 26, textAlign: "right", color: GM.vellumFaint, flex: "0 0 auto" }}>
                {r.wins}
              </span>
            </div>
          );
        })}
      </div>

      {/* THE SPOIL — only when it is actually being worn. */}
      {claim.active && (
        <div style={{
          marginTop: 8, paddingTop: 7, borderTop: `1px solid ${tint}33`,
          fontSize: 10.5, fontStyle: "italic", color: tint,
        }}>
          ◆ {t("wars.spoil", "Your faction took {week}. You fought in it — the colours are yours until the next one settles.", { week: last.key })}
        </div>
      )}
      {last?.settled?.winner && !claim.active && faction === last.settled.winner && (
        <div style={{
          marginTop: 8, paddingTop: 7, borderTop: `1px solid ${GM.pageBorder}`,
          fontSize: 10.5, fontStyle: "italic", color: GM.vellumFaint,
        }}>
          {t("wars.missedIt", "{faction} took {week} without you. Win one this week and the next set of colours is yours.",
             { faction: metaOf(last.settled.winner).short, week: last.key })}
        </div>
      )}
    </div>
  );
}
